import { Opt } from 'app/tools/general.types';
import {
  deleteStoredItem,
  retrieveStoredItem,
  storeItem,
} from 'app/tools/hooks/local-storage/local-storage-methods';
import { LSKey } from 'app/tools/hooks/local-storage/local-storage.types';
import useLocalState from 'app/tools/hooks/local-storage/use-local-storage';
import _ from 'lodash';
import { useObservableEagerState } from 'observable-hooks';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BehaviorSubject, pairwise, pluck } from 'rxjs';
import { apiActions } from '../api/api-redux';

export type AuthToken = string;

const initialAuthToken = retrieveStoredItem<AuthToken>(
  LSKey.LOGIN_OBJ
);
const authToken$ = new BehaviorSubject<Opt<AuthToken>>(
  initialAuthToken ?? undefined
);
const hadToken$ = new BehaviorSubject<boolean>(false);
authToken$
  .pipe(pairwise(), pluck(0))
  .subscribe(oldToken => hadToken$.next(!!oldToken));

//
// ------------------ Main Exports -------------------

export const getAuthToken = (): Opt<AuthToken> => authToken$.value;

export const useAuthToken = (): Opt<AuthToken> =>
  // NOTE: The '|| token$.value' is to fix a bug where sometimes
  // useObservableState returns undefined incorrectly on first run
  useObservableEagerState(authToken$) || authToken$.value;

export const deleteAuthToken = (): void => {
  deleteStoredItem(LSKey.LOGIN_OBJ);
  authToken$.next(undefined);
};

export const setAuthToken = (authToken: AuthToken): void => {
  storeItem<AuthToken>(LSKey.LOGIN_OBJ, authToken);
  authToken$.next(authToken);
};

export const useClearDataOnMissingAuthToken = (): void => {
  const dispatch = useDispatch();
  const authToken = useAuthToken();
  const hadToken = useObservableEagerState(hadToken$);
  const [, setWelcomed] = useLocalState(LSKey.WELCOMED, false);

  useEffect(() => {
    if (hadToken && !authToken) {
      setWelcomed(true);
      _.values(apiActions).forEach(slice =>
        dispatch(slice.set(undefined))
      );
    }
  }, [dispatch, authToken, hadToken, setWelcomed]);
};
